import { Button } from '@/shared/components';
import type { ParkingOccupancyEntry } from '@/shared/api';
import { parkingState } from '@/features/calendar/parkingCalendarHelpers';

const day = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;

interface Props {
  entry: ParkingOccupancyEntry & { spotLabel: string };
  confirming: boolean;
  busy: boolean;
  problem: string | null;
  onAsk: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Eine Zeile im Aufheben-Dialog: Platz, Gast oder Notiz, Zeitraum mit „frei ab“ und Stand. Aufheben
 * fragt je Zeile noch einmal nach; eine bezahlte Zeile geht nur über die Gutschrift.
 */
export function ParkingReleaseRow({ entry, confirming, busy, problem, ...props }: Props) {
  const state = parkingState(entry);
  return (
    <li className="flex flex-col gap-2 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <div className="text-ink text-sm font-semibold">
            {entry.spotLabel} ·{' '}
            {entry.guestName ?? (entry.kind === 'Block' ? 'Sperre' : 'Ohne Namen')}
          </div>
          <div className="text-ink-soft text-xs">
            {day(entry.startDate)} – frei ab {day(entry.endDate)} · {state}
          </div>
        </div>
        {/* Vom Server, nicht aus dem Status geraten: an einer bezahlten Zeile hängt eine Rechnung. */}
        {entry.paid ? (
          <span className="text-ink-soft shrink-0 text-xs">über Gutschrift</span>
        ) : confirming ? (
          <span className="flex shrink-0 items-center gap-2">
            <Button variant="ghost" className="min-h-11" disabled={busy} onClick={props.onCancel}>
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              className="min-h-11"
              loading={busy}
              disabled={busy}
              onClick={props.onConfirm}
            >
              Ja, aufheben
            </Button>
          </span>
        ) : (
          <Button
            variant="ghost"
            className="min-h-11 shrink-0"
            // Eine Anfrage zur Zeit: während eine läuft, ist jede Zeile gesperrt.
            disabled={busy}
            onClick={props.onAsk}
          >
            Aufheben
          </Button>
        )}
      </div>
      {confirming && entry.kind !== 'Block' && (
        <p className="text-amber-ink text-xs">
          Gehört die Zeile zu einer Gast-Auswahl, wird die ganze Auswahl aufgehoben – auch ihre
          anderen Plätze werden frei.
        </p>
      )}
      {problem && (
        <p role="alert" className="text-sealwax text-xs">
          {problem}
        </p>
      )}
    </li>
  );
}
